import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { createSetupIntent } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import { toast } from 'react-toastify';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const SetupForm = ({ onSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) return;

        setSubmitting(true);
        setErrorMessage(null);

        const { error } = await stripe.confirmSetup({
            elements,
            redirect: 'if_required',
        });

        if (error) {
            setErrorMessage(error.message);
            setSubmitting(false);
        } else {
            toast.success("Card added successfully!");
            onSuccess();
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement options={{
                layout: 'tabs',
                fields: {
                    billingDetails: {
                        address: 'auto',
                    },
                },
            }} />
            {errorMessage && (
                <p className="text-red-500 text-sm mt-3">{errorMessage}</p>
            )}
            <div className="flex justify-end gap-3 mt-4">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={!stripe || submitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                    {submitting ? 'Saving...' : 'Save Card'}
                </button>
            </div>
        </form>
    );
};

const AddCardForm = ({ onSuccess, onCancel }) => {
    const { userData } = useUserContext();
    const [clientSecret, setClientSecret] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSetupIntent = async () => {
            try {
                setLoading(true);
                setError(null);

                const res = await createSetupIntent(
                    userData.token,
                    userData?.selectedOrganization?.org_token
                );

                if (res?.data?.clientSecret) {
                    setClientSecret(res.data.clientSecret);
                } else {
                    setError("Failed to initialize card setup. Please try again.");
                }
            } catch (err) {
                console.error("SetupIntent error:", err);
                setError(err?.message || "Failed to initialize card setup.");
            } finally {
                setLoading(false);
            }
        };

        fetchSetupIntent();
    }, [userData?.token, userData?.selectedOrganization?.org_token]);

    if (loading) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8 shadow-inner">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Payment Method</h3>
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-500">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8 shadow-inner">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Payment Method</h3>
                <p className="text-red-500 text-sm mb-4">{error}</p>
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    if (!clientSecret) return null;

    const appearance = {
        theme: 'stripe',
    };

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8 shadow-inner">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Add New Payment Method</h3>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
                <SetupForm onSuccess={onSuccess} onCancel={onCancel} />
            </Elements>
        </div>
    );
};

export default AddCardForm;
